import { Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { AttendanceService } from '../../services/attendance.service';
import { ImportConflictDialogComponent } from './import-conflict-dialog/import-conflict-dialog.component';

interface ImportedIndividualPunchRecord {
  personId: string;
  personName: string;
  punchDate: string;
  attendanceRecord: string;
  verifyType: string;
  timeZone: string;
  source: string;
  rawData: any;
}

@Component({
  selector: 'app-attendance-individual-punch-import',
  templateUrl: './attendance-individual-punch-import.component.html',
  styleUrls: ['./attendance-individual-punch-import.component.scss']
})
export class AttendanceIndividualPunchImportComponent {
  loading = false;
  importSummary: string = '';
  importedRecords: ImportedIndividualPunchRecord[] = [];
  errors: string[] = [];

  constructor(
    private snackBar: MatSnackBar, 
    private attendanceService: AttendanceService,
    private dialog: MatDialog
  ) {}

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    this.loading = true;
    this.errors = [];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
      // Parse rows and map to individual punch records
      const punchRecords = this.parseRows(rows);
      this.importedRecords = punchRecords;
      // Check for conflicts first
      this.checkForConflicts(punchRecords);
    };
    reader.readAsArrayBuffer(file);
  }

  parseRows(rows: any[][]): ImportedIndividualPunchRecord[] {
    const records: ImportedIndividualPunchRecord[] = [];
    if (rows.length < 2) {
      console.warn('Excel file has insufficient rows');
      return records;
    }

    // Find the header row dynamically (look for a row with 'Person ID' and 'Punch Date')
    let headerRowIdx = -1;
    let header: string[] = [];
    for (let i = 0; i < Math.min(10, rows.length); i++) {
      const row = rows[i].map((h: any) => h?.toString().trim().toLowerCase());
      if (row.includes('person id') && row.includes('punch date')) {
        headerRowIdx = i;
        header = row;
        break;
      }
    }
    
    if (headerRowIdx === -1) {
      console.error('Could not find header row with both "Person ID" and "Punch Date" in the first 10 rows.');
      return records;
    }
    
    console.log(`Detected header row at index ${headerRowIdx}:`, header);
    
    // Map column indices
    const personIdIdx = header.indexOf('person id');
    const personNameIdx = header.indexOf('person name');
    const punchDateIdx = header.indexOf('punch date');
    const attendanceRecordIdx = header.indexOf('attendance record');
    const verifyTypeIdx = header.indexOf('verify type');
    const timeZoneIdx = header.indexOf('timezone');
    const sourceIdx = header.indexOf('source');

    if (personIdIdx === -1 || punchDateIdx === -1 || attendanceRecordIdx === -1) {
      console.error('Required columns not found: Person ID, Punch Date, Attendance Record');
      return records;
    }

    for (let i = headerRowIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) {
        console.warn(`Skipping row ${i + 1}: Empty row`);
        continue;
      }

      const personId = row[personIdIdx]?.toString().trim() || '';
      const punchDate = row[punchDateIdx]?.toString().trim() || '';
      const attendanceRecord = row[attendanceRecordIdx]?.toString().trim() || '';

      if (!personId || !punchDate || !attendanceRecord) {
        console.warn(`Skipping row ${i + 1}: Missing required data`, { personId, punchDate, attendanceRecord, row });
        continue;
      }

      // Validate date format - try multiple approaches
      let isValidDateTime = false;
      
      // Try parsing as combined string first
      const dateTimeStr = `${punchDate} ${attendanceRecord}`;
      if (!isNaN(Date.parse(dateTimeStr))) {
        isValidDateTime = true;
      } else {
        // Try parsing date and time separately
        const dateObj = new Date(punchDate);
        if (!isNaN(dateObj.getTime())) {
          // Date is valid, now check if time is valid
          const timeParts = attendanceRecord.split(':');
          if (timeParts.length === 3) {
            const hours = parseInt(timeParts[0]);
            const minutes = parseInt(timeParts[1]);
            const seconds = parseInt(timeParts[2]);
            if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59) {
              isValidDateTime = true;
            }
          }
        }
      }
      
      if (!isValidDateTime) {
        console.warn(`Skipping row ${i + 1}: Invalid date/time format`, { punchDate, attendanceRecord, dateTimeStr, row });
        continue;
      }

      records.push({
        personId,
        personName: row[personNameIdx]?.toString().trim() || '',
        punchDate,
        attendanceRecord,
        verifyType: row[verifyTypeIdx]?.toString().trim() || '',
        timeZone: row[timeZoneIdx]?.toString().trim() || '',
        source: row[sourceIdx]?.toString().trim() || '',
        rawData: row
      });
    }

    console.log(`Parsed ${records.length} valid individual punch records from ${rows.length - (headerRowIdx + 1)} data rows`);
    return records;
  }

  checkForConflicts(records: ImportedIndividualPunchRecord[]) {
    if (records.length === 0) {
      this.loading = false;
      this.importSummary = 'No valid records found to import';
      this.snackBar.open(this.importSummary, 'Close', { duration: 3000 });
      return;
    }

    // Convert to backend format
    const importData = records.map(record => ({
      personId: record.personId,
      personName: record.personName,
      punchDate: record.punchDate,
      attendanceRecord: record.attendanceRecord,
      verifyType: record.verifyType,
      timeZone: record.timeZone,
      source: record.source
    }));

    this.attendanceService.detectImportConflicts(importData).subscribe({
      next: (response) => {
        this.loading = false;
        
        if (response.hasConflicts) {
          // Show conflict dialog
          this.showConflictDialog(response, importData);
        } else {
          // No conflicts, proceed with import
          this.importIndividualPunchRecords(importData, false);
        }
      },
      error: (error) => {
        this.loading = false;
        this.importSummary = `Error checking for conflicts: ${error.error?.message || error.message}`;
        this.snackBar.open(this.importSummary, 'Close', { duration: 5000 });
      }
    });
  }

  showConflictDialog(conflictResponse: any, importData: any[]) {
    const dialogRef = this.dialog.open(ImportConflictDialogComponent, {
      width: '600px',
      data: {
        conflicts: conflictResponse.conflictingDates,
        message: conflictResponse.message
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        // User made a decision (true = overwrite, false = skip)
        this.importIndividualPunchRecords(importData, result);
      } else {
        // User cancelled
        this.importSummary = 'Import cancelled by user';
        this.snackBar.open(this.importSummary, 'Close', { duration: 3000 });
      }
    });
  }

  importIndividualPunchRecords(importData: any[], overwriteExistingData: boolean) {
    this.loading = true;

    this.attendanceService.importIndividualPunchRecords(importData, overwriteExistingData).subscribe({
      next: (response) => {
        this.loading = false;
        this.errors = response.errors || [];
        
        let summaryMessage = `Successfully imported ${response.importedCount} individual punch records.`;
        if (response.skippedCount > 0) {
          summaryMessage += ` ${response.skippedCount} duplicates skipped.`;
        }
        if (overwriteExistingData) {
          summaryMessage += ' Existing data was overwritten.';
        }
        
        this.importSummary = summaryMessage;
        this.snackBar.open(this.importSummary, 'Close', { duration: 5000 });
      },
      error: (error) => {
        this.loading = false;
        this.importSummary = `Import failed: ${error.error?.message || error.message}`;
        this.snackBar.open(this.importSummary, 'Close', { duration: 5000 });
      }
    });
  }
}
